/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
import {
  ACCOUNT_MAILING_ADDRESSES,
  USER_REGION_SETTINGS_QUERY_KEY,
} from '../../../../../utilities/sharedQueries';
import { ORDERABLE_CARD_TYPES } from '../../../queries';
import { useSetting } from '../../../../../utilities/sharedHooks/settings';
import {
  CARD_NETWORKS,
  cardOrderFeesKeys,
  mainCardTypes,
  resolveInitialOrderScreen,
  UK_COUNTRY,
} from '../../../constants';
import { useLedgerFilteredWallets } from '../../../../../utilities/sharedHooks/ledgerWallets';
import { WALLETS_ALL } from '../../../../Accounts/queries';
import { showAlert } from '../../../../../utilities/notifications';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import {
  useUserCardProvider,
  useUserPersonalInfo,
} from '../../../../../utilities/sharedHooks/user';
import { GenericLoading } from '../../../../../components/Placeholders';
import useEligibleForFreeCard from '../../../../../utilities/sharedHooks/eligibleForFreeCard';
import MastercardLogo from '../../../../../assets/images/mastercard.svg';
import VisaLogo from '../../../../../assets/images/visa.svg';
import VisaUSLogo from '../../../../../assets/images/visa-logo.svg';

const MainLayout = ({ children = null, className = '' }) => {
  const history = useHistory();

  const {
    state,
    openScreen,
    updateCardOrderSettings,
    updateUserWallets,
    updateChosenWalletForPayment,
    updateCardProvider,
    updateCardType,
    updateUser,
    updateCardHolderName,
    updateUserRegionSettings,
    updateCardCurrency,
    updateFreeCardEligible,
    updateCardOptions,
    updateCardOrderFee,
    updateAccountMailingAddress,
    updateIsReplacementCard,
    updateOrdarableCardType,
    updateMainCardType,
    updateCardIssueOptions,
    updateCardNetwork,
    updateCardProvidersWithTypes,
  } = useContext(OrderCardContext);

  const cardType = state?.cardType;

  const { state: locationState } = useLocation();

  const { data: cardOptionsData } = useQuery(ORDERABLE_CARD_TYPES);

  const { cardProvider } = useUserCardProvider();
  const { isEligible, isWaitinglist, isLoading } = useEligibleForFreeCard(
    locationState?.isCardReplacement,
    state?.cardType
  );

  const user = useUserPersonalInfo();

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  const { data: mailingAddresses, isLoading: isLoadingMailingAddresses } =
    useQuery(ACCOUNT_MAILING_ADDRESSES);

  const orderableCardTypes = useMemo(() => {
    if (cardOptionsData?.orderableCardTypes) {
      return [
        ...new Set(
          cardOptionsData.orderableCardTypes
            .flatMap((provider) => provider.cards || [])
            .map((card) => card.type)
            .filter(Boolean)
        ),
      ];
    }
    return [];
  }, [cardOptionsData]);

  const cardOptions = useMemo(() => {
    const options = {};
    if (cardOptionsData && cardOptionsData.orderableCardTypes) {
      cardOptionsData.orderableCardTypes.forEach((item) => {
        const { cards } = item;
        const visaLogo = user?.isUS ? VisaUSLogo : VisaLogo;
        options[item.provider.toLowerCase()] = cards.map((card) => ({
          name: card.processor,
          type: card.type,
          provider: item.provider.toLowerCase(),
          image: item?.processor === CARD_NETWORKS.MASTERCARD ? MastercardLogo : visaLogo,
        }));
      });
    }
    return options;
  }, [cardOptionsData, user]);

  const cardProvidersWithTypesObj = useMemo(() => {
    let result = null;
    if (
      cardOptionsData &&
      userRegionSettings &&
      cardOptionsData.orderableCardTypes &&
      cardOptionsData.orderableCardTypes.length > 0
    ) {
      result = {};
      cardOptionsData?.orderableCardTypes.forEach((item) => {
        const { cards } = item;
        if (cards) {
          if (cards.length === 1) {
            result[item.provider.toLowerCase()] = {
              [cards[0].type]: cards[0].processor,
            };
          } else if (cards.length > 1) {
            result[item.provider.toLowerCase()] = {};
            cards.forEach((card) => {
              result[item.provider.toLowerCase()][card.type] = card.processor;
            });
          }
        }
      });
    }
    return result;
  }, [userRegionSettings, cardOptionsData]);

  const defaultNetwork = useMemo(() => {
    if (cardProvidersWithTypesObj && userRegionSettings?.default_card_type) {
      if (cardProvidersWithTypesObj[cardType]) {
        if (
          state?.mainCardType &&
          state?.mainCardType?.toUpperCase() in cardProvidersWithTypesObj[cardType]
        ) {
          return cardProvidersWithTypesObj[cardType][state?.mainCardType?.toUpperCase()];
        }
        if (userRegionSettings?.default_card_type in cardProvidersWithTypesObj[cardType]) {
          return cardProvidersWithTypesObj[cardType][userRegionSettings?.default_card_type];
        }
      }
    }
    return 'MASTERCARD';
  }, [cardProvidersWithTypesObj, cardType, state?.mainCardType, userRegionSettings]);

  const [serverSettings] = useSetting('server_configurations', [
    cardOrderFeesKeys.ROW,
    cardOrderFeesKeys.US,
    cardOrderFeesKeys.UK,
    cardOrderFeesKeys.EU,
    'support_email_address',
    'promo_amount_free_card',
    'promo_amount_free_card_currency',
    'stable_coin_can_fund_by_card',
  ]);

  const [contentUrlSettings] = useSetting('content_urls', ['cardholder_term_galileo_us_user']);

  const [uiSettings] = useSetting('ui_features_toggles', [
    'ui_feature_show_hints',
    'feature_pgw_warning_message',
    'feature_allow_pgw_warning',
  ]);

  const [appSettings] = useSetting('app_features', [
    'feature_allow_card_order_payment_crypto',
    'feature_allow_card_order_payment_stablecoin',
    'feature_pgw_allowed',
  ]);

  const [pgwSettings] = useSetting('payment_gateway_options', ['pgw_allow_crypto_exchange']);

  const { data: userWallets } = useLedgerFilteredWallets(WALLETS_ALL, {
    onSettled(data, error) {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const chosenWalletForPayment = useMemo(
    () =>
      userWallets
        ? userWallets.find((w) => w.coin === state?.payment.currency?.toLowerCase())
        : null,
    [userWallets, state?.payment?.currency]
  );

  const cardOrderFeeKey = useMemo(() => {
    let key = '';
    if (user && state?.mainCardType === mainCardTypes.PHYSICAL) {
      if (user?.isUS) {
        key = cardOrderFeesKeys.US;
      } else if (user?.countryDisplayName === UK_COUNTRY) {
        key = cardOrderFeesKeys.UK;
      } else {
        key = cardOrderFeesKeys.EU;
      }
    } else {
      key = cardOrderFeesKeys.ROW;
    }

    return key;
  }, [user, state]);

  const [cardHolderFirstName, cardHolderLastName] = useMemo(
    () => [user.firstName, user.lastName],
    [user, cardProvider]
  );

  useEffect(() => {
    if (cardOrderFeeKey && state?.cardOrderSettings.server) {
      updateCardOrderFee(state?.cardOrderSettings.server?.[cardOrderFeeKey]);
    }
  }, [cardOrderFeeKey, state?.cardOrderSettings?.server]);

  useEffect(() => {
    if (serverSettings && state?.cardOrderSettings?.server === null) {
      updateCardOrderSettings('server', serverSettings);
    }
  }, [serverSettings, state?.cardOrderSettings?.server]);

  useEffect(() => {
    if (uiSettings && state?.cardOrderSettings?.ui === null) {
      updateCardOrderSettings('ui', uiSettings);
    }
  }, [uiSettings, state?.cardOrderSettings?.ui]);

  useEffect(() => {
    if (appSettings && state?.cardOrderSettings?.app === null) {
      updateCardOrderSettings('app', appSettings);
    }
  }, [appSettings, state?.cardOrderSettings?.app]);

  useEffect(() => {
    if (pgwSettings && state?.cardOrderSettings?.pgw === null) {
      updateCardOrderSettings('pgw', pgwSettings);
    }
  }, [pgwSettings, state?.cardOrderSettings?.pgw]);

  useEffect(() => {
    if (contentUrlSettings && state?.cardOrderSettings?.contentUrls === null) {
      updateCardOrderSettings('contentUrls', contentUrlSettings);
    }
  }, [contentUrlSettings, state?.cardOrderSettings?.contentUrls]);

  useEffect(() => {
    if (userRegionSettings && state?.regionSettings === null) {
      updateUserRegionSettings(userRegionSettings);
    }
  }, [userRegionSettings, state?.regionSettings]);

  useEffect(() => {
    updateFreeCardEligible({ isEligible, isWaitinglist });
  }, [isEligible, isWaitinglist]);

  useEffect(() => {
    if (cardOptions && state?.cardOptions === null) {
      updateCardOptions(cardOptions);
    }
  }, [state?.cardOptions, cardOptions]);

  useEffect(() => {
    if (user && state?.cardCurrency?.coin === null) {
      updateCardCurrency(user?.defaultCardCurrency);
    }
  }, [user, state?.cardCurrency?.coin]);

  useEffect(() => {
    if (cardHolderFirstName && cardHolderLastName && !state?.cardHolderName) {
      updateCardHolderName({ firstName: cardHolderFirstName, lastName: cardHolderLastName });
    }
  }, [cardHolderFirstName, cardHolderLastName]);

  useEffect(() => {
    if (user && !state?.user) {
      updateUser(user);
    }
  }, [user]);

  useEffect(() => {
    if (defaultNetwork && (!state?.cardNetwork || state?.cardNetwork !== defaultNetwork)) {
      updateCardNetwork(defaultNetwork);
    }
  }, [defaultNetwork]);

  useEffect(() => {
    if (cardProvidersWithTypesObj && !state?.cardProvidersWithTypesObj) {
      updateCardProvidersWithTypes(cardProvidersWithTypesObj);
    }
  }, [cardProvidersWithTypesObj, state?.cardProvidersWithTypesObj]);

  useEffect(() => {
    if (userWallets) {
      updateUserWallets(userWallets);
    }
  }, [userWallets]);

  useEffect(() => {
    if (userWallets && chosenWalletForPayment) {
      updateChosenWalletForPayment(chosenWalletForPayment);
    }
  }, [userWallets]);

  useEffect(() => {
    // TODO: Need to be updated when we support allowing choose between  multiple card providers
    if (cardProvider && !state?.cardType) {
      updateCardType(cardProvider);
    }
  }, [state?.cardType, cardProvider]);

  useEffect(() => {
    if (cardProvider) {
      updateCardProvider(cardProvider);
    }
  }, [cardProvider]);

  useEffect(() => {
    if (mailingAddresses && !state?.accountMailingAddress) {
      updateAccountMailingAddress(mailingAddresses);
    }
  }, [mailingAddresses, state?.accountMailingAddress]);

  useEffect(() => {
    if (state?.isCardReplacement === null) {
      updateIsReplacementCard(locationState?.isCardReplacement);
    }
  }, [locationState?.isCardReplacement, state?.isCardReplacement]);

  useEffect(() => {
    if (!state?.mainCardType) {
      if (locationState?.mainCardType) {
        updateMainCardType(locationState?.mainCardType);
      } else if (
        cardProvider &&
        cardProvider in cardOptions &&
        cardOptions[cardProvider]?.length === 1
      ) {
        updateMainCardType(cardOptions[cardProvider][0].type?.toLowerCase());
      } else if (userRegionSettings && userRegionSettings?.default_card_type) {
        updateMainCardType(userRegionSettings?.default_card_type?.toLowerCase());
      }
    }
  }, [state?.mainCardType, userRegionSettings, locationState?.mainCardType]);

  useEffect(() => {
    if (locationState?.cardIssueOptions && !state?.cardIssueOptions) {
      updateCardIssueOptions(locationState?.cardIssueOptions);
    }
  }, [state?.cardIssueOptions, locationState?.cardIssueOptions]);

  useEffect(() => {
    if (orderableCardTypes && state?.orderableCardTypes === null) {
      updateOrdarableCardType(orderableCardTypes);
    }
  }, [orderableCardTypes, state?.orderableCardTypes]);

  useEffect(() => {
    if (!state?.currentScreen && orderableCardTypes && cardProvider) {
      const { screen, options } = resolveInitialOrderScreen({
        cardProvider,
        cardType: state.cardType,
        orderableCardTypes,
        isWaitinglist,
        isCardReplacement: locationState?.isCardReplacement,
      });
      openScreen(screen, options);
    }
  }, [
    orderableCardTypes,
    cardProvider,
    state.currentScreen,
    state.mainCardType,
    isWaitinglist,
    isLoading,
    state.cardType,
    cardProvidersWithTypesObj,
  ]);

  const renderedComponent = useMemo(() => {
    if (
      !state.cardProvider ||
      !state?.cardType ||
      !state.user ||
      !state.cardOrderSettings.server ||
      !state.cardOrderSettings.ui ||
      !state?.regionSettings ||
      !cardProvider ||
      !orderableCardTypes ||
      isLoadingMailingAddresses ||
      isLoading
    ) {
      return <GenericLoading isFullScreen={false} />;
    }
    if (Array.isArray(children)) {
      return children?.filter((child) => child.props.keyName === state?.currentScreen);
    }
    return [children]?.find((child) => child.props.keyName === state?.currentScreen);
  }, [
    state.cardProvider,
    state.user,
    state.cardType,
    state.cardOrderSettings.server,
    state.cardOrderSettings.ui,
    state?.regionSettings,
    state?.currentScreen,
    orderableCardTypes,
    cardProvider,
    children,
    isLoadingMailingAddresses,
    isLoading,
  ]);

  const closeCardOrder = () => history.push('/dashboard/card');

  return (
    <div className="d-flex flex-row main-layout">
      {/* browser only */}
      <div
        className="d-none d-md-block position-absolute"
        onClick={closeCardOrder}
        onKeyDown={closeCardOrder}
        role="button"
        style={{ top: '4%', right: '3%', zIndex: 1 }}
        tabIndex={-1}>
        <i
          className="close-btn cursor-pointer icon icon-close-other text-dark text-f4"
          aria-label="Close"
        />
      </div>
      <div className="d-flex flex-column flex-fill align-items-center h-100vh position-relative">
        <div
          className={`d-flex flex-column w-100 position-relative h-100 ${className} card-order-main-layout`}
          style={{
            maxWidth: '33.5rem',
            transitionProperty: 'margin',
            transitionDuration: '300ms',
          }}>
          {renderedComponent}
        </div>
      </div>
    </div>
  );
};

export default MainLayout;

MainLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string,
};
